import { useState } from 'react';
import { AnonymousProfile } from '../types/Profile';

interface ProfileEditorProps {
  profile: AnonymousProfile;
  onUpdate: (updates: Partial<Omit<AnonymousProfile, 'id' | 'createdAt'>>) => void;
  onClose: () => void;
}

const PRESET_COLORS = [
  'from-purple-400 to-pink-400',
  'from-blue-400 to-purple-500',
  'from-green-400 to-blue-500', 
  'from-yellow-400 to-orange-500',
  'from-pink-400 to-red-500',
  'from-indigo-400 to-purple-500',
  'from-teal-400 to-blue-500',
  'from-orange-400 to-pink-500',
  'from-cyan-400 to-blue-500',
  'from-lime-400 to-green-500'
];

const PRESET_EMOJIS = [
  '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🥲',
  '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰',
  '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜',
  '🤪', '🤨', '🧐', '🤓', '😎', '🥸', '🤩', '🥳'
];

export default function ProfileEditor({ profile, onUpdate, onClose }: ProfileEditorProps) {
  const [displayName, setDisplayName] = useState(profile.displayName);
  const [selectedColor, setSelectedColor] = useState(profile.avatarColor);
  const [selectedEmoji, setSelectedEmoji] = useState(profile.avatarEmoji);

  const handleSave = () => {
    onUpdate({
      displayName: displayName.trim(),
      avatarColor: selectedColor,
      avatarEmoji: selectedEmoji
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">프로필 수정</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* 프로필 미리보기 */}
          <div className="text-center">
            <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-br ${selectedColor} flex items-center justify-center text-2xl mb-3`}>
              {selectedEmoji}
            </div>
            <h3 className="font-medium text-gray-900">{displayName}</h3>
          </div>

          {/* 닉네임 수정 */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              닉네임
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={20}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="닉네임을 입력하세요"
            />
            <p className="text-xs text-gray-500">
              {displayName.length}/20자
            </p>
          </div>

          {/* 아바타 색상 선택 */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              아바타 색상
            </label>
            <div className="grid grid-cols-5 gap-3">
              {PRESET_COLORS.map((color, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedColor(color)}
                  className={`w-12 h-12 rounded-full bg-gradient-to-br ${color} border-2 transition-all ${
                    selectedColor === color 
                      ? 'border-gray-900 scale-110' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* 이모지 선택 */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              아바타 이모지
            </label>
            <div className="grid grid-cols-8 gap-2">
              {PRESET_EMOJIS.map((emoji, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedEmoji(emoji)}
                  className={`w-10 h-10 rounded-lg border-2 text-lg transition-all ${
                    selectedEmoji === emoji 
                      ? 'border-blue-500 bg-blue-50 scale-110' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 하단 버튼 */}
        <div className="p-4 border-t border-gray-200 flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={!displayName.trim()}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );
}